import { useCallback, useEffect, useRef } from 'react';
import { fetchReviewState, ReviewUrls, triggerAiScore } from '../utils/curationApi';
import { ReviewAction } from './curationReviewReducer';

export const POLL_INTERVAL_MS = 2500;
// Must outlast the server's own budget (a 120s LLM call, retried by Sidekiq),
// or the pane reports a timeout on a run that is still going and will land in
// scoring_failed by itself.
export const POLL_TIMEOUT_MS = 600000;

// POST-then-poll delivery of the AI match score. One place to touch if this
// ever moves to ActionCable (CvProcessingChannel is the precedent).
export const useAiScore = (
  urls: ReviewUrls,
  dispatch: React.Dispatch<ReviewAction>,
  aiStatus: string,
) => {
  const timerRef = useRef<number | null>(null);
  const deadlineRef = useRef<number>(0);

  const stopPolling = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Single owner of the timer: scheduling always cancels whatever was pending,
  // so start and the resume effect can never run two poll chains at once.
  const schedulePoll = useCallback(
    (run: () => void) => {
      stopPolling();
      timerRef.current = window.setTimeout(run, POLL_INTERVAL_MS);
    },
    [stopPolling],
  );

  const poll = useCallback(async () => {
    try {
      const server = await fetchReviewState(urls);
      if (server.status === 'scored' && server.ai.payload) {
        dispatch({ payload: server.ai.payload, type: 'AI_LOADED' });
        return;
      }
      if (server.status === 'scoring_failed') {
        dispatch({ error: server.ai.error || 'scoring_failed', type: 'AI_FAILED' });
        return;
      }
    } catch {
      // A transient poll failure is retried until the deadline.
    }

    if (Date.now() >= deadlineRef.current) {
      dispatch({ error: 'timeout', type: 'AI_FAILED' });
      return;
    }
    schedulePoll(poll);
  }, [urls, dispatch, schedulePoll]);

  const start = useCallback(async () => {
    stopPolling();
    dispatch({ type: 'AI_LOADING' });
    deadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    try {
      await triggerAiScore(urls);
    } catch {
      dispatch({ error: 'trigger_failed', type: 'AI_FAILED' });
      return;
    }
    schedulePoll(poll);
  }, [urls, dispatch, poll, schedulePoll, stopPolling]);

  // A page opened while scoring was already running resumes polling.
  useEffect(() => {
    if (aiStatus === 'loading' && timerRef.current === null) {
      deadlineRef.current = deadlineRef.current || Date.now() + POLL_TIMEOUT_MS;
      schedulePoll(poll);
    }
    return stopPolling;
  }, [aiStatus]);

  return { start };
};
